import { z } from 'zod';

const isAddress = (s: string): s is `0x${string}` =>
  !!s.match(/^0x[a-fA-F0-9]{40}$/);

const parsing = z
  .object({
    ENVIRONMENT: z.enum(['local', 'develop', 'staging', 'release']),
    // Public front-end build: the data layer answers locally instead of
    // calling Supabase, and account-only surfaces are not offered. Unset, the
    // app behaves exactly as before.
    SHELL_MODE: z
      .preprocess((v) => v === '1' || v === 'true', z.boolean())
      .default(false),
    RELEASE: z.string().optional(),
    RELEASE_VERSION: z.string().optional(),
    SUPABASE_URL: z.string().url(),
    SUPABASE_ANON_KEY: z.string(),
    SERVER: z.string().url().optional(),
    ORIGIN: z.string().url().optional(),
    COMMIT: z.string().optional(),
    COMMIT_DATE: z
      .preprocess(
        (s) => (typeof s == 'string' ? new Date(s) : undefined),
        z.date(),
      )
      .optional(),
    SENTRY_DSN: z.string().url(),
    WALLETCONNECT_PROJECT_ID: z.string(),
    TWITTER_CLIENT_ID: z.string(),
    ETHEREUM_RPC_WEBSOCKET: z.string().url(),
    ETHEREUM_RPC_HTTP: z.string().url().optional(),
    ETHEREUM_PERSONA_ADDRESS: z.string().refine(isAddress),
    ETHEREUM_BRIDGE_ADDRESS: z.string().refine(isAddress),
    BASE_RPC_WEBSOCKET: z.string().url(),
    BASE_RPC_HTTP: z.string().url().optional(),
    BASE_PERSONA_ADDRESS: z.string().refine(isAddress),
    BASE_STAKING_ADDRESS: z.string().refine(isAddress),
  })
  .transform((data) => ({
    ...data,
    ethereum: {
      RPC_WEBSOCKET: data.ETHEREUM_RPC_WEBSOCKET,
      RPC_HTTP: data.ETHEREUM_RPC_HTTP,
      PERSONA_ADDRESS: data.ETHEREUM_PERSONA_ADDRESS,
      BRIDGE_ADDRESS: data.ETHEREUM_BRIDGE_ADDRESS,
    },
    base: {
      RPC_WEBSOCKET: data.BASE_RPC_WEBSOCKET,
      RPC_HTTP: data.BASE_RPC_HTTP,
      PERSONA_ADDRESS: data.BASE_PERSONA_ADDRESS,
      STAKING_ADDRESS: data.BASE_STAKING_ADDRESS,
    },
  }))
  .safeParse({
    ENVIRONMENT: process.env.NEXT_PUBLIC_ENVIRONMENT,
    SHELL_MODE: process.env.NEXT_PUBLIC_SHELL_MODE,
    RELEASE: process.env.NEXT_PUBLIC_RELEASE,
    RELEASE_VERSION: process.env.NEXT_PUBLIC_RELEASE_VERSION,
    ORIGIN: process.env.NEXT_PUBLIC_ORIGIN,
    SERVER: process.env.NEXT_PUBLIC_SERVER,
    SUPABASE_URL: process.env.NEXT_PUBLIC_SUPABASE_URL,
    SUPABASE_ANON_KEY: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY,
    COMMIT: process.env.NEXT_PUBLIC_COMMIT,
    COMMIT_DATE: process.env.NEXT_PUBLIC_COMMIT_DATE,
    SENTRY_DSN: process.env.NEXT_PUBLIC_SENTRY_DSN,
    WALLETCONNECT_PROJECT_ID: process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID,
    TWITTER_CLIENT_ID: process.env.NEXT_PUBLIC_TWITTER_CLIENT_ID,
    ETHEREUM_RPC_WEBSOCKET: process.env.NEXT_PUBLIC_ETHEREUM_RPC_WEBSOCKET,
    ETHEREUM_RPC_HTTP: process.env.NEXT_PUBLIC_ETHEREUM_RPC_HTTP,
    ETHEREUM_PERSONA_ADDRESS: process.env.NEXT_PUBLIC_ETHEREUM_PERSONA_ADDRESS,
    ETHEREUM_BRIDGE_ADDRESS: process.env.NEXT_PUBLIC_ETHEREUM_BRIDGE_ADDRESS,
    BASE_RPC_WEBSOCKET: process.env.NEXT_PUBLIC_BASE_RPC_WEBSOCKET,
    BASE_RPC_HTTP: process.env.NEXT_PUBLIC_BASE_RPC_HTTP,
    BASE_PERSONA_ADDRESS: process.env.NEXT_PUBLIC_BASE_PERSONA_ADDRESS,
    BASE_STAKING_ADDRESS: process.env.NEXT_PUBLIC_BASE_STAKING_ADDRESS,
  });

if (!parsing.success) {
  throw new Error(`Invalid env configuration: ${parsing.error.message}`);
}

export const env = parsing.data;
